
import { useMeteoData } from './Hooks/useMeteoData';

import moment from "moment";

import { Observer } from 'astronomy-engine';

import { sunInfo, moonInfo, h2hm, data2series, isDayTime} from "./utils/functions";

import Webcam from './components/Webcam';

import {useUTCTime, useDates} from './Hooks/Dates';
import Chart from './components/Chart';

import config from "./config.json";
import InfoCard from './components/InfoCard';

import WeatherDashboard from './WeatherDashboard';
import Alerts from './components/Alerts';

import Staralt from './components/Staralt';

// Lee settings del storage; si no hay, usa los valores de config.json
const appSettings = JSON.parse(localStorage.getItem('appSettings')) || {};
const stations = appSettings.stations || config.stations;
let default_station = appSettings.primary_station || config.primary_station;

function Dashboard() {

  const utc = useUTCTime();
  const dates = useDates();

  const { data, loading, error } = useMeteoData(stations, "PT6H");

  // reestructura los datos meteorologicos para usarlos
  const series = data2series(data, stations, config.sensors);

  // Mostrar loading o error si es necesario
  const hasData = Object.keys(data).length > 0;

  if (loading && !hasData) {
    return <Alerts loading={loading} />  // Solo bloquea en la carga inicial
  }

  // Datos del sol y la luna
  const observer = new Observer(config.location.latitude,
                              config.location.longitude,
                              config.location.elevation,
  );

   // Tiempos y horas
  const now_utc = moment.utc().toDate();
  const now_utc_next_day = moment().add(1, 'day').utc().toDate();

  const sun_info = sunInfo(observer, now_utc);
  const sun_info_next_day = sunInfo(observer, now_utc_next_day);
  const moon_info = moonInfo(observer, now_utc);

  // calculo de la duración de la noche astronomica en horas
  const astro_dusk2down_hours = sun_info_next_day.astronomical_twilight_down.diff(sun_info.astronomical_twilight_dusk, 'hours', true);

  let latest_datetime = moment.utc(data[default_station]['latestData']['time']);
  let now = moment.utc();

  // la estación principal falla o está atascada (más viejos de 20min), usa la estación secundaria
  if (now.diff(latest_datetime, 'minutes') > 20) {
    default_station = config.secondary_station;
  }

  return (

    <div>
      
      {/* ---- Meteo ------ */}
      {/* ---- Allways stella for dust and song for seeing ------ */}
      <WeatherDashboard datos_meteo={data[default_station]['latestData']} stella_data={data['stella']?.latestData ?? []} song_data={data['song']?.latestData ?? []} config={config} sun_info={sun_info} moon_info={moon_info} dates={dates} station={default_station} />

      {/* ---- Graficos ------ */}

      <div className="my-3" id="dashboard-plots">
     
          <div className="row gx-1 mt-4 mb-2" style={{ margin: '0 auto', height: 300 + 'px !important'}}>

            <div className='col-md-7'>
              {isDayTime() ? (
                <Webcam webcamUrl="https://izana.aemet.es/wp-content/rtime/camaras/sona_dia.jpg" title="CIAI day allsky 07:00-19:00 UTC" />
              ) : (
                <Webcam webcamUrl="https://research.iac.es/OOCC/tiempo/speculoos_allsky/AllSkyCurrentImage.JPG" title="SPECULOOS-N" />
              )}
            </div>
            <div className='col-md-5'>
                <Chart
                    title="Wind Speed"
                    seriesName="Wind Speed (m/s)"
                    data={series.wind_speed}
                    limits={{'hard': config.limits.wind_speed.hard}}
                    yAxisLabel="m/s"
                    xtickInterval={1 * 60 * 60 * 1000} // Intervalo de 1 hora
                    ytickInterval={1}
                />
                <Chart
                    title="Humidity"
                    seriesName="Humidity (%RH)"
                    data={series.humidity}
                    yAxisLabel="%RH"
                    limits={{'hard': config.limits.humidity.hard}}
                    ylim={[0, 100]}
                    xtickInterval={1 * 60 * 60 * 1000} // Intervalo de 1 hora
                    ytickInterval={25}
                />
                <Chart
                    title="Dust"
                    seriesName="Dust"
                    data={series.dust}
                    limits={{'hard': config.limits.dust.hard }}
                    yAxisLabel="m⁻³"
                    ylim={[0, 0.09]}
                    xtickInterval={1 * 60 * 60 * 1000} // Intervalo de 1 hora
                    ytickInterval={0.005}
                />
            </div>
          </div>

      </div>


      {/* ---- Sol y crepusculos ------ */}

      <div className='row mx-2 my-4'>
          <InfoCard title="Sun set time" label="Sunset" icon_name="" value={sun_info.sunset.format('HH:mm')} unit="UT" variant='bg-sienna col' />
          <InfoCard title="Nautical twilight dusk (Astronomical twilight start)" label="Nautical dusk end" value={sun_info.nautical_twilight_dusk.toISOString().substring(11, 16)} unit="UT" variant='bg-indigo col' />
          <InfoCard title="Astronomical twilight dusk (Astronomical night start)" label="Astro dusk end" value={sun_info.astronomical_twilight_dusk.toISOString().substring(11, 16)} unit="UT" variant='bg-verydarkgray col' />
          <InfoCard label="Astronomical night" title="" value={h2hm(astro_dusk2down_hours)} unit="" variant='bg-dark col-3' />
          <InfoCard title="Astronomical twilight down (Astronomical night end, nautical twilight start)" label="Astro down end" value={sun_info_next_day.astronomical_twilight_down.toISOString().substring(11, 16)} unit="UT" variant='bg-verydarkgray col' />
          <InfoCard title="Nautical twilight down (Nautical twilight end, civil twilight start)" label="Nautical down end" value={sun_info_next_day.nautical_twilight_down.toISOString().substring(11, 16)} unit="UT" variant='bg-indigo col' />
          <InfoCard title="Sun rise time" label="Sunrise" icon_name="" value={sun_info_next_day.sunrise.format('HH:mm')} unit="UT" variant='bg-sienna col' />
      </div>

      {/* ---- Luna ------ */}

      <div className="row my-4">
        <div className="col-1 text-end" style={{width: '40px', marginLeft: '30px'}}>
          <i class="bi bi-moon float-right"></i>
        </div>
        <div className="col-11">
          <InfoCard label="Moon rise" title="Moon rise (UT)" value={moon_info.moonrise.format('HH:mm') + (moon_info.moonrise_today == true ? "" : " (+1d)")} variant='bg-darkslategrey p-1' unit=" UT" inline="True" />
          <InfoCard label="Moon set" title="Moon set (UT)" value={moon_info.moonset.format('HH:mm') + (moon_info.moonset_today == true ? "" : " (+1d)")} variant='bg-darkslategrey p-1' unit=" UT" inline="True" />
          <InfoCard label="Moon altitude" title="Moon altitude in degrees" value={ Number.parseFloat(moon_info.altitude).toFixed(1) } variant='bg-darkslategrey p-1' unit="&deg;" inline="True" />
        </div>
      </div>

      {/* ---- Staralt ------ */}
      <Staralt observer_location={config.location}
                obsdate_yyyymmdd={utc.format('YYYY-MM-DD')}
                night_mode="False"
                minimum_altitude={-20}
                xtick_interval = {30}
                div_height="300px"
       />


    </div>
  );
}

export default Dashboard;
